import React, { useEffect } from "react"

// Zapier's Workflow Element: our Zap templates and "create from scratch", inside the app.
// Zapier opens in a popup on our page, including signup for users without an account.
// Its list of the user's Zaps is off: it covers all their events and, in team workspaces,
// shows owners' names; managing Zaps stays on zapier.com.
// The files define the <zapier-workflow> custom element; the options come from Zapier's
// generator at https://zapier.com/partner/embed/workflow.
// https://docs.zapier.com/powered-by-zapier/embedding-zapier/workflow-element
const SCRIPT_URL = "https://cdn.zapier.com/packages/partner-sdk/v0/zapier-elements/zapier-elements.esm.js"
const STYLESHEET_URL = "https://cdn.zapier.com/packages/partner-sdk/v0/zapier-elements/zapier-elements.css"

declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      "zapier-workflow": React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement> & {
        "client-id": string
        theme?: "light" | "dark"
        "intro-copy-display"?: "show" | "hide"
        "manage-zaps-display"?: "show" | "hide"
        "guess-zap-display"?: "show" | "hide"
        "app-search-bar-display"?: "show" | "hide"
        "zap-create-from-scratch-display"?: "show" | "hide"
      }
    }
  }
}

const appendOnce = (selector: string, create: () => HTMLElement) => {
  if (!document.querySelector(selector)) document.head.appendChild(create())
}

// Idempotent: called when the user shows intent (hover, focus, click) and when the element mounts
export const loadZapierElements = () => {
  appendOnce(`script[src="${SCRIPT_URL}"]`, () => Object.assign(document.createElement("script"), {
    type: "module", src: SCRIPT_URL,
  }))
  appendOnce(`link[href="${STYLESHEET_URL}"]`, () => Object.assign(document.createElement("link"), {
    rel: "stylesheet", href: STYLESHEET_URL,
  }))
}

type Props = {
  // Diobox's embed client ID, from Zapier's generator, per environment. It's public: the page
  // carries it, and Zapier only accepts it on the domains registered for our embed.
  clientId: string
}

const ZapierWorkflowElement = ({ clientId }: Props) => {
  useEffect(loadZapierElements, [])

  return (
    <zapier-workflow
      client-id={clientId}
      theme="light"
      intro-copy-display="show"
      manage-zaps-display="hide"
      guess-zap-display="hide"
      app-search-bar-display="show"
      zap-create-from-scratch-display="show"
    />
  )
}

export default ZapierWorkflowElement
